import { GenericTableColumn, GenericTableAction, GenericTable } from '../../../../../models/GenericCRUD/GenericTable';
import { GenericFormField, GenericForm } from '../../../../../models/GenericCRUD/GenericForm';
import { Filter } from '../../../../../models/GenericCRUD/Filter';
import { Paginator } from '../../../../../models/Paginator';

// Importar las configuraciones de entidades
import { banksConfig } from './banks.config';
import { movementsConfig } from './cash-movements.config';
import { cashClosuresConfig } from './cash-closures.config';
import { chartOfAccountsConfig } from './chart-of-accounts.config';
import { invoicesConfig } from './invoices.config';
import { taxRatesConfig } from './tax-rates.config';
import { posTerminalConfig } from './pos-terminal.config';

/**
 * Interface que define la configuración completa de una entidad administrativa
 */
export interface EntityConfig {
  id: string;                          // Identificador único ('banks', 'movements', etc.)
  name: string;                        // Nombre para mostrar ('Bancos', 'Movimientos', etc.)
  icon: string;                        // SVG icon como string HTML
  tableColumns: GenericTableColumn[];  // Columnas de la tabla
  formFields: GenericFormField[];      // Campos del formulario de creación
  formFieldsEdit?: GenericFormField[]; // Campos opcionales para edición
  form: GenericForm;                   // Configuración del formulario
  table: GenericTable<Paginator<any>>; // Configuración de la tabla
  filters: Filter[];                   // Definición de filtros
  tableActions: GenericTableAction[];  // Acciones disponibles (editar, eliminar, etc.)
  useModalForm: boolean;               // Modal (true) o fullscreen (false)
  createRoute?: string;                // Ruta custom para creación (en vez de form inline)
}

/**
 * Metadata para los tabs de navegación
 */
export interface EntityMetadata {
  id: string;
  name: string;
  icon: string;
  order: number;
}

/**
 * Registro central de todas las entidades administrativas
 */
export const ADMIN_ENTITIES: { [key: string]: EntityConfig } = {
  'banks': banksConfig,
  'movements': movementsConfig,
  'cash-closures': cashClosuresConfig,
  'chart-of-accounts': chartOfAccountsConfig,
  'invoices': invoicesConfig,
  'tax-rates': taxRatesConfig,
  'pos-terminals': posTerminalConfig
};

/**
 * Metadatos para los tabs de navegación
 */
export const ENTITY_METADATA: EntityMetadata[] = [
  {
    id: 'banks',
    name: 'Bancos',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>',
    order: 1
  },
  {
    id: 'movements',
    name: 'Movimientos',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/></svg>',
    order: 2
  },
  {
    id: 'cash-closures',
    name: 'Arqueo y Cierre',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>',
    order: 3
  },
  {
    id: 'chart-of-accounts',
    name: 'Plan de Cuentas',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
    order: 4
  },
  {
    id: 'invoices',
    name: 'Facturas',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
    order: 5
  },
  {
    id: 'tax-rates',
    name: 'Tipos de IVA',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>',
    order: 6
  },
  {
    id: 'pos-terminals',
    name: 'TPVs',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>',
    order: 7
  }
];
